import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import carConfigurationSelectors from '../../../../store/selectors/carConfigurationSelectors';
import { IImage } from '../../../../../common/model/ICarDetails';
import { ICarDetails } from '../../../../../../../common/model/ICarDetails';
import { IPrice } from '../../../../../../../common/model/IPrice';
import RefreshPostcodeCheckContainer from '../../postcodeCheck/RefreshPostcodeCheckContainer';
import Summary from './summarySection/Summary';
import currentDealSelectors from '../../../../store/selectors/currentDealSelectors';
import { ImageType } from '../../../../../common/enum/ImageType';
import ScrollToWrapper from '../../../presentational/ScrollToWrapper';
import { CustomiseSections } from '../../../../../common/enum/CustomiseSections';
import { carConfigurationActions } from '../../../../store/reducers/carConfigurationReducer';
import scrollInView, { ScrollInViewType } from '../../../../../common/service/scrollInView';
import { getVariantDetailsExteriors } from '../../../../store/selectors/variantDetailsSelectors';
import { IExteriorColour } from '../../../../../../../common/model/IColours';
import { usePersistedCurrencyPreference } from '../../../../react/usePersistedCurrencyPreference';
import optionsSelectors from '../../../../store/selectors/optionsSelectors';
import { IBasketInitRequest } from '../../../../api/basketApi';
import useGetPostCode from '../../../../../common/react/useGetPostCode';
import usePlatformConfig from '../../../../../common/react/usePlatformConfig';
import thunkActions from '../../../../store/actions/thunkActions';
import ICarConfigOption from '../../../../../../../common/model/ICarConfigOption';
import configurationSourceUrlService from '../../../../service/configurationSourceUrlService';
import { useParams } from 'react-router';
import useWidgetContext from '../../../../../common/react/useWidgetContext';
import { IParams } from '../../../../model/IParams';
import { CAR_CONFIG_PRICE_TYPES } from '../../../../constants/carConfig';
import useCustomerStorage from '../../../../../common/hooks/useCustomerStorage';

const extractImage = (images: IImage[], type: string): IImage | null =>
  images?.find((image: IImage) => image?.type?.toLowerCase() === type.toLowerCase()) || null;

const SummaryContainer: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const configuration: ICarDetails = useSelector(carConfigurationSelectors.getConfiguration);

  const primaryPrice: IPrice = useSelector(currentDealSelectors.getPrimaryPrice);
  const secondaryPrice: IPrice = useSelector(currentDealSelectors.getSecondaryPrice);
  const servicePlan = useSelector(currentDealSelectors.getServicePlan);
  const accessories = useSelector(currentDealSelectors.getAccessories);
  const accessoryPacks = useSelector(currentDealSelectors.getAccessoryPacks);
  const { isSecondaryCurrencyDisplay } = usePersistedCurrencyPreference();
  const isMultiCurrency = useSelector(currentDealSelectors.getIsMultiCurrency);
  const currency: string =
    isMultiCurrency && isSecondaryCurrencyDisplay ? secondaryPrice?.currency : primaryPrice?.currency;
  const imageUrl: string | null = extractImage(configuration?.exteriorColour?.images, ImageType.FRONT)?.url;
  const exteriorColours = useSelector(getVariantDetailsExteriors);
  const selectedExterior = useSelector(carConfigurationSelectors.getSelectedExteriorColour);
  const optionsError = useSelector(optionsSelectors.getOptionsErrorState);
  const postcode = useGetPostCode();
  const {
    platformConfig: { priceDisplay, isComplexModel },
  } = usePlatformConfig();
  const car = useSelector(carConfigurationSelectors.getCustomisedCar);
  const { modelId } = useParams<IParams>();
  const {
    configuration: { basePath, dimensions },
  } = useWidgetContext();
  const { customerStorage } = useCustomerStorage();

  const filteredSelectedExterior = useMemo(() => {
    if (!selectedExterior?.hexColourCode) {
      return exteriorColours?.find((color: IExteriorColour) => selectedExterior?.id === color.id);
    }
  }, [selectedExterior, exteriorColours]);

  const removeAction = useCallback(
    async (category: string, optionId?: string) => {
      let initRequest: IBasketInitRequest;
      const partialInitRequest = {
        vehicleId: car.carId,
        dealerId: customerStorage.getCustomerData()?.dealerId || '',
        postcode,
      };
      switch (category) {
        case CustomiseSections.ACCESSORIES: {
          dispatch(carConfigurationActions.removeAccessory(optionId));

          const selectedAccessories: string[] = accessories.reduce(
            (ids: string[], accessory: ICarConfigOption) => (accessory.id === optionId ? ids : [...ids, accessory.id]),
            [],
          );
          initRequest = {
            ...partialInitRequest,
            vehicleAccessories: selectedAccessories,
            vehicleAccessoryPacks: accessoryPacks.map((pack: ICarConfigOption) => pack.id) || [],
            vehicleServicePlan: servicePlan?.id || null,
            configurationSourceUrl: configurationSourceUrlService.getUrl(basePath, modelId, car.carId, {
              ...car,
              accessories: selectedAccessories,
            }),
          };
          break;
        }
        case CustomiseSections.PACKS: {
          dispatch(carConfigurationActions.removeAccessoryPack(optionId));

          const selectedPacks: string[] = accessoryPacks.reduce(
            (ids: string[], pack: ICarConfigOption) => (pack.id === optionId ? ids : [...ids, pack.id]),
            [],
          );
          initRequest = {
            ...partialInitRequest,
            vehicleAccessories: accessories.map((accessory: ICarConfigOption) => accessory.id) || [],
            vehicleAccessoryPacks: selectedPacks,
            vehicleServicePlan: servicePlan?.id || null,
            configurationSourceUrl: configurationSourceUrlService.getUrl(basePath, modelId, car.carId, {
              ...car,
              packs: selectedPacks,
            }),
          };
          break;
        }
        case CustomiseSections.SERVICE_PLAN: {
          dispatch(carConfigurationActions.removeServicePlan());

          initRequest = {
            ...partialInitRequest,
            vehicleAccessories: accessories.map((accessory: any) => accessory.id) || [],
            vehicleAccessoryPacks: accessoryPacks.map((pack: any) => pack.id) || [],
            vehicleServicePlan: null,
            configurationSourceUrl: configurationSourceUrlService.getUrl(basePath, modelId, car.carId, {
              ...car,
              servicePlan: null,
            }),
          };
          break;
        }
        default: {
          return;
        }
      }

      const { payload } = (await dispatch(thunkActions.initBasket({ initRequest, priceDisplay, dimensions }))) as any;
      if (payload) {
        // @TODO: Refactor when deal decoupled from basket.
        dispatch(carConfigurationActions.setDataFromDeal(payload.deal));
      }
    },
    [accessories, accessoryPacks, servicePlan, car, postcode, priceDisplay, modelId, basePath],
  );

  const toggleSummaryView = useCallback(
    (shouldExpand: boolean) => {
      setIsExpanded(shouldExpand);
    },
    [isExpanded],
  );

  useEffect(() => {
    const selectionsSection: HTMLElement = document.getElementById(CustomiseSections.SELECTIONS);
    if (isExpanded && selectionsSection) {
      scrollInView.scrollInView(selectionsSection, ScrollInViewType.ANY);
    }
  }, [isExpanded]);

  const govChargesPrice = useMemo(() => {
    if (configuration) {
      return configuration.prices.find(price => price.type === CAR_CONFIG_PRICE_TYPES.FULL)?.governmentCharges || 0;
    }
    return 0;
  }, [configuration]);

  return (
    <ScrollToWrapper target={CustomiseSections.SUMMARY} data-test={'container:section:summary'}>
      <RefreshPostcodeCheckContainer />
      <Summary
        loading={!configuration || !primaryPrice}
        error={optionsError}
        setIsExpanded={toggleSummaryView}
        isExpanded={isExpanded}
        imageUrl={imageUrl}
        currency={currency}
        exteriorColour={filteredSelectedExterior ?? selectedExterior}
        interiorColour={configuration?.interiorColour}
        servicePlan={servicePlan}
        accessories={accessories}
        accessoryPacks={accessoryPacks}
        removeAction={removeAction}
        govCharges={govChargesPrice}
        isSecondaryCurrencyDisplay={isSecondaryCurrencyDisplay}
        isComplexModel={isComplexModel}
      />
    </ScrollToWrapper>
  );
};

export default SummaryContainer;
